import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import Konva from 'konva';
import { RootState } from 'store';
import { useRefs } from 'contexts/RefContext';
import { getImagerySourceLabel } from '../../util';
import { RoofImageBuilder } from 'components/tool/utils/konvaUtil';

import { getBoundingBox } from 'components/DisplayRoofData/api';
import { selectAllEdges, selectFilteredGutterLines } from 'store/slices/RoofDataSlice/roofSelector';
import { getRoofDesignData } from 'components/DisplayRoofData/utils';

export function useSaveRoofDesign() {
	const { konvaRef } = useRefs();

	const {
		uuid, org, imgHeight, imgWidth, latLng, imagerySource,
		allRoofSegs, groundMetersPerPixel, showAutoDetectedEdges,
	} = useSelector((state: RootState) => state.roofData.data);

	const { fullScreenEnabled } = useSelector(
		(state: RootState) => state.toolNewPostions.data
	);
	const isB2C = !!org?.setting?.isB2C;

	const allRoofEdges = useSelector(selectAllEdges);
	const filteredGutterLines = useSelector(selectFilteredGutterLines);

	const averageRoofHeight = useMemo(() => {
		const hasMissingHeight = allRoofSegs.some(
			(seg) => !seg?.isGroundMount && seg.roofHeight == null
		);
		if (hasMissingHeight) return null;
		const segs = allRoofSegs.filter((s) => !s.isGroundMount && s.roofHeight != null);
		if (segs.length === 0) return null;
		return segs.reduce((acc, seg) => acc + (seg.roofHeight ?? 0), 0) / segs.length;
	}, [allRoofSegs]);

	const bboxQuery = useQuery(
		'roof-bounding-box',
		async () => {
			const { bbox } = await getBoundingBox(uuid);
			return bbox;
		},
		{ cacheTime: 0, enabled: false, refetchOnMount: false, retry: 3 }
	);

	const saveRoofDesign = useCallback(async () => {
		const [lat, lng] = latLng.split(',');
		const bbox = (await bboxQuery.refetch()).data;
		const images = new RoofImageBuilder({
			stage: konvaRef?.current as Konva.Stage,
			fullScreenEnabled,
			groundMetersPerPixel,
			ogImgDims: { width: imgWidth, height: imgHeight },
			houseBoundingBox: bbox ?? undefined,
		})
			.withOutput({ pixelRatio: 0.8, mimeType: 'image/png' })
			.getFullImage({ mimeType: isB2C ? 'image/png' : 'image/jpeg' })
			.getLabelImage()
			.result();

		return {
			design: {
				...getRoofDesignData(allRoofSegs, allRoofEdges, filteredGutterLines, showAutoDetectedEdges),
				img: images?.baseImage ?? '',
				imgWithFacetLabel: images?.labelsOnly ?? '',
				lat: +lat, lng: +lng,
				imagerySource: getImagerySourceLabel(imagerySource),
				averageRoofHeight,
				designUUID: uuid,
			},
		};
	}, [latLng, bboxQuery, konvaRef, fullScreenEnabled, groundMetersPerPixel,
		imgWidth, imgHeight, isB2C, allRoofSegs, allRoofEdges, filteredGutterLines,
		showAutoDetectedEdges, imagerySource, uuid, averageRoofHeight]);

	return { saveRoofDesign };
}
